import apiClient from './client';

// ===== PRODUCTS =====

/**
 * Get all products with filters and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 20)
 * @param {string} params.search - Search query
 * @param {number} params.brandId - Filter by brand
 * @param {number} params.sizeId - Filter by size
 * @param {number} params.useCaseId - Filter by use case
 * @param {number} params.typeId - Filter by tile type
 * @param {boolean} params.isActive - Filter by active status
 * @param {boolean} params.isFeatured - Filter by featured status
 * @param {number} params.minPrice - Minimum price filter
 * @param {number} params.maxPrice - Maximum price filter
 * @param {boolean} params.inStock - Filter only in-stock products
 * @param {string} params.sortBy - Sort field (default: created_at)
 * @param {string} params.sortOrder - Sort order: ASC or DESC (default: DESC)
 */
export const getProducts = async (params = {}) => {
    const response = await apiClient.get('/products', {
        params,
        headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
        }
    });
    return response.data;
};

/**
 * Get single product by ID
 */
export const getProductById = async (id) => {
    const response = await apiClient.get(`/products/${id}`);
    return response.data;
};

/**
 * Create new product
 */
export const createProduct = async (data) => {
    // Check if data is FormData (for file uploads)
    const config = data instanceof FormData ? {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    } : {};

    const response = await apiClient.post('/products', data, config);
    return response.data;
};

/**
 * Update product
 */
export const updateProduct = async (id, data) => {
    // Check if data is FormData (for file uploads)
    const config = data instanceof FormData ? {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    } : {};

    const response = await apiClient.put(`/products/${id}`, data, config);
    return response.data;
};

/**
 * Delete product (soft delete)
 */
export const deleteProduct = async (id) => {
    const response = await apiClient.delete(`/products/${id}`);
    return response.data;
};

/**
 * Update product stock
 * @param {number} id - Product ID
 * @param {number} quantity - Quantity to update
 * @param {string} operation - Operation: 'set', 'add', or 'subtract'
 */
export const updateProductStock = async (id, quantity, operation = 'set') => {
    const response = await apiClient.patch(`/products/${id}/stock`, {
        quantity,
        operation
    });
    return response.data;
};

/**
 * Get products with low stock
 */
export const getLowStockProducts = async () => {
    const response = await apiClient.get('/products/low-stock');
    return response.data;
};

/**
 * Get product statistics
 */
export const getProductStatistics = async () => {
    const response = await apiClient.get('/products/statistics');
    return response.data;
};

/**
 * Add stock to product (additive operation)
 * @param {number} id - Product ID
 * @param {number} quantity - Quantity to add
 * @param {string} reason - Reason for stock addition
 */
export const addStock = async (id, quantity, reason) => {
    const response = await apiClient.post(`/products/${id}/add-stock`, {
        quantity,
        reason
    });
    return response.data;
};

/**
 * Get stock history for a product
 * @param {number} id - Product ID
 */
export const getStockHistory = async (id) => {
    const response = await apiClient.get(`/products/${id}/stock-history`);
    return response.data;
};

/**
 * Generate product code based on categories
 * @param {number} brandId - Brand ID
 * @param {number} typeId - Tile Type ID
 * @param {number} sizeId - Size ID
 */
export const generateProductCode = async (brandId, typeId, sizeId) => {
    const response = await apiClient.post('/products/generate-code', {
        brandId,
        typeId,
        sizeId
    });
    return response.data;
};

/**
 * Upload product image
 * @param {File} file - Image file
 */
export const uploadProductImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await apiClient.post('/products/upload-image', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return response.data;
};

/**
 * Upload multiple product images
 * @param {FileList} files - Image files
 */
export const uploadProductImages = async (files) => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
        formData.append('images', file);
    });

    const response = await apiClient.post('/products/upload-images', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return response.data;
};

/**
 * Check if product code exists
 * @param {string} code - Product code to check
 * @returns {Promise<{exists: boolean, product: object|null}>}
 */
export const checkProductCode = async (code) => {
    const response = await apiClient.post('/products/check-code', { code });
    return response.data;
};

/**
 * Get branch stock breakdown for a product (for multi-branch sales)
 * @param {number} id - Product ID
 * @returns {Promise<{product_id, branches: [{branchId, branchName, stockQuantity}]}>}
 */
export const getProductBranchStock = async (id) => {
    const response = await apiClient.get(`/products/${id}/branch-stock`);
    return response.data;
};

export default {
    getProducts,
    getProductById,
    createProduct,
    updateProduct,
    deleteProduct,
    updateProductStock,
    getLowStockProducts,
    getProductStatistics,
    addStock,
    getStockHistory,
    generateProductCode,
    uploadProductImage,
    uploadProductImages,
    checkProductCode,
    getProductBranchStock
};
